import { ApiRequestPaging, ApiResponse } from '@interfaces/api';
import {
  BrandsInterface,
  CatalogInterface,
  CreateProductDraftRequestInterface,
  CreateProductDraftResponseInterface,
  ModelInterface,
  PriceRecommendationInterface,
  ProductDraftAttributesInterface,
  ProductDraftRequestPayload,
} from '@interfaces/models/deposit';
import { axios, getAxiosConfig } from '@config/axios';
import Environment from '@config/index';
import { ProductDraftsResponse } from '@interfaces/models/productDrafts';
import logger from '@helpers/utils/logger/client';

export default class DepositService {
  private static baseUrl = Environment.apiBaseUrl;

  static async listCatalog(): Promise<CatalogInterface[]> {
    const axiosConfig = getAxiosConfig();
    const {
      data: { data },
    } = await axios.get<ApiResponse<CatalogInterface[]>>(`${this.baseUrl}/deposit/catalog`, axiosConfig);
    return data;
  }

  static async listBrands(): Promise<BrandsInterface[]> {
    const axiosConfig = getAxiosConfig();
    try {
      const {
        data: { data },
      } = await axios.get<ApiResponse<BrandsInterface[]>>(`${this.baseUrl}/deposit/brands`, axiosConfig);

      return data;
    } catch (err) {
      logger.error(err, 'Error fetching brands. Server responded with : ');
      return [];
    }
  }

  static async createPreduct(payload: Partial<ProductDraftRequestPayload>): Promise<string> {
    const axiosConfig = getAxiosConfig();
    const {
      data: { data },
    } = await axios.post(`${this.baseUrl}/deposit/addPreduct`, payload, axiosConfig);

    return data?.id;
  }

  static async listModels({ categoryId, brandId }: Record<string, string>): Promise<ModelInterface[]> {
    const axiosConfig = getAxiosConfig();

    try {
      const {
        data: { data },
      } = await axios.get<ApiResponse<ModelInterface[]>>(
        `${this.baseUrl}/deposit/models?categoryId=${categoryId}&brandId=${brandId}`,
        axiosConfig,
      );

      return data;
    } catch (err) {
      logger.error(err, 'Error getting models for selected brand. The server responded with: ');
      return err;
    }
  }

  static async createProductDraft(payload: CreateProductDraftRequestInterface): Promise<string> {
    const axiosConfig = getAxiosConfig();

    try {
      const {
        data: { data },
      } = await axios.post<ApiResponse<CreateProductDraftResponseInterface>>(
        `${this.baseUrl}/deposit/products/drafts`,
        payload,
        axiosConfig,
      );

      return data.draftId;
    } catch (err) {
      logger.error(err, 'Error creating the product draft. The server responded with: ');
      return err;
    }
  }

  static async getPriceEstimate(
    payload: Partial<ProductDraftAttributesInterface>,
  ): Promise<PriceRecommendationInterface> {
    const axiosConfig = getAxiosConfig();

    let params = '?';
    for (const [key, value] of Object.entries(payload)) {
      if (value) {
        params += `${key}=${value}&`;
      }
    }

    const {
      data: { data },
    } = await axios.get<ApiResponse<PriceRecommendationInterface>>(
      `${this.baseUrl}/deposit/resaleCalculator${params}`,
      axiosConfig,
    );

    return data === undefined ? ({} as PriceRecommendationInterface) : data;
  }

  static async getProductDraftsList({ offset, limit }: ApiRequestPaging): Promise<ProductDraftsResponse> {
    const axiosConfig = getAxiosConfig();

    axiosConfig.params.offset = offset;
    axiosConfig.params.limit = limit;

    const {
      data: { data },
    } = await axios.get(`${this.baseUrl}/users/me/product-drafts`, axiosConfig);
    return data;
  }

  static async deleteProductDraft(productDraftId: number): Promise<void> {
    const axiosConfig = getAxiosConfig();
    return axios.delete(`${this.baseUrl}/product-listing/product-drafts/${productDraftId}`, axiosConfig);
  }
}
